<template>
  <div>
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          Novo Registro
        </div>
      </div>
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-4">
            <label><span class="required">*</span> Colaborador</label>
            <v-select
              v-model="vacation.user_id"
              :options="availableUsers"
              label="name"
              :reduce="user => user.id"
              placeholder="Pesquise ou cole o nome do colaborador"
              :loading="loadingUsers"
              clearable
            />
          </div>
          <div class="col-md-4">
            <label><span class="required">*</span> Início das Férias</label>
            <div class="input-group">
              <input
                ref="start_date_input"
                v-mask="'##/##/####'"
                type="text"
                class="form-control"
                :value="dateInputs.start_date"
                placeholder="DD/MM/AAAA"
                inputmode="numeric"
                autocomplete="off"
                @input="handleDateInput('start_date', $event)"
                @blur="normalizeDateField('start_date')"
              >
              <div class="input-group-append">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="openDatePicker('start_date')"
                >
                  <i class="fa fa-calendar" />
                </button>
              </div>
            </div>
            <input
              ref="start_date_picker"
              type="date"
              class="native-date-picker"
              :value="vacation.start_date"
              tabindex="-1"
              aria-hidden="true"
              @input="handleNativeDateChange('start_date', $event.target.value)"
            >
          </div>
          <div class="col-md-4">
            <label><span class="required">*</span> Fim das Férias</label>
            <div class="input-group">
              <input
                ref="end_date_input"
                v-mask="'##/##/####'"
                type="text"
                class="form-control"
                :value="dateInputs.end_date"
                placeholder="DD/MM/AAAA"
                inputmode="numeric"
                autocomplete="off"
                @input="handleDateInput('end_date', $event)"
                @blur="normalizeDateField('end_date')"
              >
              <div class="input-group-append">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="openDatePicker('end_date')"
                >
                  <i class="fa fa-calendar" />
                </button>
              </div>
            </div>
            <input
              ref="end_date_picker"
              type="date"
              class="native-date-picker"
              :value="vacation.end_date"
              :min="vacation.start_date || null"
              tabindex="-1"
              aria-hidden="true"
              @input="handleNativeDateChange('end_date', $event.target.value)"
            >
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <label><span class="required">*</span> Número de Dias</label>
            <input
              v-model="vacation.number_of_days"
              type="number"
              class="form-control"
              min="1"
              step="1"
              inputmode="numeric"
              readonly
            >
            <small class="text-muted">Calculado automaticamente a partir do início e fim das férias.</small>
          </div>
          <div class="col-md-4">
            <label><span class="required">*</span> Abono Pecuniário</label>
            <select
              v-model="vacation.pecuniary_allowance"
              class="form-control"
            >
              <option value="1">
                Sim
              </option>
              <option value="0">
                Não
              </option>
            </select>
          </div>
        </div>
        <div class="row mt-2" v-if="!isEditing">
          <div class="col-md-4">
            <button type="button" class="btn btn-info" @click="addToStaging">
              <i class="fa fa-plus"></i> Adicionar
            </button>
          </div>
        </div>
        <div class="row mt-3" v-if="!isEditing && stagedVacations.length">
          <div class="col-md-12">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Nº Dias</th>
                  <th>Abono</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in stagedVacations" :key="index">
                  <td>{{ row.userName }}</td>
                  <td>{{ formatDateForDisplay(row.start_date) }}</td>
                  <td>{{ formatDateForDisplay(row.end_date) }}</td>
                  <td>{{ row.number_of_days }}</td>
                  <td>{{ row.pecuniary_allowance === '1' ? 'Sim' : 'Não' }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-crud delete"
                      title="Remover"
                      @click="removeStagedRow(index)"
                    >
                      <i class="far fa-trash-alt"></i>
                    </button>
                    <small v-if="row.error" class="text-danger d-block mt-1">{{ row.error }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="block text-right mt-2">
          <router-link
            to="/vacations"
            class="btn btn-back"
          >
            <i class="fa fa-arrow-left" /> Voltar
          </router-link>
          <a
            class="btn btn-add ml-1"
            @click="isEditing ? edit() : save()"
          >
            {{ isEditing ? 'Atualizar' : 'Salvar' }}
            <i class="fa fa-save" />
          </a>
        </div>
        <div class="row">
          <div class="col-md-4">
            <p><span class="required">*</span> Campos Obrigatórios</p>
          </div>
        </div>
      </div>
    </div>
    <b-modal v-model="showConflictModal" title="Não foi possível salvar" size="lg" hide-footer>
      <p>
        Os registros não foram salvos porque os colaboradores abaixo já possuem férias
        cadastradas em um período que se sobrepõe ao informado:
      </p>
      <table class="table table-sm table-bordered">
        <thead>
          <tr>
            <th>Colaborador</th>
            <th>Período já registrado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(conflict, index) in conflicts" :key="index">
            <td>{{ conflict.employeeName }}</td>
            <td>{{ conflict.periodStart }} a {{ conflict.periodEnd }}</td>
          </tr>
        </tbody>
      </table>
      <div class="text-right">
        <button type="button" class="btn btn-secondary" @click="showConflictModal = false">
          Fechar
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { apiErrorMessage } from '@/utils/apiError'

export default {
  computed: {
    isEditing() {
      return !!this.$route.params.id;
    },
    availableUsers() {
      return this.users.filter(
        (user) => !this.stagedVacations.some((row) => row.user_id === user.id)
      );
    },
  },
  data() {
    return {
      users: [],
      loadingUsers: false,
      dateInputs: {
        start_date: '',
        end_date: '',
      },
      vacation: {
        user_id: null,
        start_date: '',
        end_date: '',
        number_of_days: '',
        pecuniary_allowance: '0',
      },
      stagedVacations: [],
      showConflictModal: false,
      conflicts: [],
    };
  },
  watch: {
    'vacation.start_date': function (value) {
      this.dateInputs.start_date = this.formatDateForDisplay(value)
      this.updateNumberOfDays()
    },
    'vacation.end_date': function (value) {
      this.dateInputs.end_date = this.formatDateForDisplay(value)
      this.updateNumberOfDays()
    },
  },
  mounted() {
    const self = this

    self.getUsers()
    self.dateInputs.start_date = self.formatDateForDisplay(self.vacation.start_date)
    self.dateInputs.end_date = self.formatDateForDisplay(self.vacation.end_date)

    const { id } = self.$route.params
    if (id) {
      self.getVacation(id)
    }
  },
  methods: {
    formatDateForApi(value) {
      if (!value || typeof value !== 'string') return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      return this.parseDisplayDate(value)
    },
    normalizeVacationPayload(payload = {}) {
      const normalizedStartDate = this.formatDateForApi(payload.start_date || '')
      const normalizedEndDate = this.formatDateForApi(payload.end_date || '')

      return {
        ...this.vacation,
        ...payload,
        id: payload.id || null,
        user_id: payload.user_id || null,
        start_date: normalizedStartDate,
        end_date: normalizedEndDate,
        number_of_days: payload.number_of_days ? String(payload.number_of_days) : '',
        pecuniary_allowance: payload.pecuniary_allowance != null ? String(payload.pecuniary_allowance) : '0',
      }
    },
    applyDateMask(input) {
      const digits = (input || '').replace(/[^0-9]/g, '').slice(0, 8)
      if (!digits) return ''
      if (digits.length <= 2) return digits
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
    },
    formatDateForDisplay(value) {
      if (!value || typeof value !== 'string') return ''
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!match) return value
      return `${match[3]}/${match[2]}/${match[1]}`
    },
    parseDisplayDate(value) {
      if (!value || typeof value !== 'string') return ''
      const trimmed = value.trim()
      const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (!match) return ''

      const day = parseInt(match[1], 10)
      const month = parseInt(match[2], 10)
      const year = parseInt(match[3], 10)
      const date = new Date(year, month - 1, day)

      if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
      ) {
        return ''
      }

      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    },
    isValidDisplayDate(value) {
      return !!this.parseDisplayDate(value)
    },
    isDateRangeInvalid(startDate = this.vacation.start_date, endDate = this.vacation.end_date) {
      if (!startDate || !endDate) return false

      const start = new Date(`${startDate}T00:00:00`)
      const end = new Date(`${endDate}T00:00:00`)

      return end < start
    },
    updateNumberOfDays() {
      if (!this.vacation.start_date || !this.vacation.end_date || this.isDateRangeInvalid()) {
        this.vacation.number_of_days = ''
        return
      }

      const start = new Date(`${this.vacation.start_date}T00:00:00`)
      const end = new Date(`${this.vacation.end_date}T00:00:00`)
      const millisecondsPerDay = 24 * 60 * 60 * 1000
      const totalDays = Math.floor((end - start) / millisecondsPerDay) + 1

      this.vacation.number_of_days = String(totalDays)
    },
    resetEndDateWithError() {
      this.vacation.end_date = ''
      this.dateInputs.end_date = ''
      this.vacation.number_of_days = ''

      this.$nextTick(() => {
        const endInput = this.getDateInputRef('end_date')
        if (endInput) {
          endInput.focus()
        }
      })

      this.$message('Erro', 'A data final das férias não pode ser menor que a data inicial.', 'error')
    },
    enforceDateRange(changedField) {
      if (!this.isDateRangeInvalid()) return

      if (changedField === 'start_date') {
        this.resetEndDateWithError()
        return
      }

      this.resetEndDateWithError()
    },
    getDateInputRef(field) {
      const input = this.$refs[`${field}_input`]
      return Array.isArray(input) ? input[0] : input
    },
    getCursorPositionFromDigits(value, digitsBeforeCursor) {
      if (!digitsBeforeCursor) return 0

      let digitCount = 0
      for (let index = 0; index < value.length; index += 1) {
        if (/\d/.test(value[index])) {
          digitCount += 1
        }

        if (digitCount >= digitsBeforeCursor) {
          return index + 1
        }
      }

      return value.length
    },
    restoreDateCursor(field, digitsBeforeCursor) {
      const input = this.getDateInputRef(field)
      if (!input || document.activeElement !== input) return

      const cursorPosition = this.getCursorPositionFromDigits(input.value, digitsBeforeCursor)
      input.setSelectionRange(cursorPosition, cursorPosition)
    },
    handleDateInput(field, event) {
      const input = event.target
      const { value } = input
      const selectionStart = input.selectionStart ?? value.length
      const digitsBeforeCursor = value.slice(0, selectionStart).replace(/\D/g, '').length
      const masked = this.applyDateMask(value)

      this.dateInputs[field] = masked

      const parsed = this.parseDisplayDate(masked)
      if (parsed) {
        this.vacation[field] = parsed
      } else if (!masked || !masked.trim()) {
        this.vacation[field] = ''
      }

      if (parsed) {
        this.enforceDateRange(field)
      }

      this.$nextTick(() => {
        this.restoreDateCursor(field, digitsBeforeCursor)
      })
    },
    normalizeDateField(field) {
      this.dateInputs[field] = this.applyDateMask(this.dateInputs[field])
      const parsed = this.parseDisplayDate(this.dateInputs[field])
      if (parsed) {
        this.vacation[field] = parsed
        this.dateInputs[field] = this.formatDateForDisplay(parsed)
        this.enforceDateRange(field)
      } else if (!this.dateInputs[field]) {
        this.vacation[field] = ''
      }
    },
    handleNativeDateChange(field, value) {
      this.vacation[field] = value || ''
      this.dateInputs[field] = this.formatDateForDisplay(value)
      if (value) {
        this.enforceDateRange(field)
      }
    },
    openDatePicker(field) {
      const refName = `${field}_picker`
      const picker = this.$refs[refName]
      if (!picker) return

      if (typeof picker.showPicker === 'function') {
        picker.showPicker()
        return
      }

      picker.focus()
      picker.click()
    },
    validateForm(row = this.vacation, dateInputs = this.dateInputs) {
      if (!row.user_id) {
        this.$message("Erro", "Selecione o colaborador.", "error");
        return false;
      }

      if (!dateInputs.start_date || !this.isValidDisplayDate(dateInputs.start_date)) {
        this.$message("Erro", "Informe uma data de início válida no formato DD/MM/AAAA.", "error");
        return false;
      }

      if (!dateInputs.end_date || !this.isValidDisplayDate(dateInputs.end_date)) {
        this.$message("Erro", "Informe uma data de fim válida no formato DD/MM/AAAA.", "error");
        return false;
      }

      const startDate = new Date(`${row.start_date}T00:00:00`);
      const endDate = new Date(`${row.end_date}T00:00:00`);

      if (endDate < startDate) {
        this.$message('Erro', 'A data final das férias não pode ser menor que a data inicial.', 'error')
        return false
      }

      const numberOfDays = Number(row.number_of_days);
      if (!Number.isInteger(numberOfDays) || numberOfDays <= 0) {
        this.$message('Erro', 'Informe um número de dias válido.', 'error')
        return false
      }

      return true
    },
    resetFormFields() {
      this.vacation = {
        user_id: null,
        start_date: "",
        end_date: "",
        number_of_days: "",
        pecuniary_allowance: "0",
      };
      this.dateInputs = { start_date: "", end_date: "" };
    },
    addToStaging() {
      this.normalizeDateField("start_date");
      this.normalizeDateField("end_date");

      if (!this.validateForm()) return;

      const selectedUser = this.users.find((user) => user.id === this.vacation.user_id);

      this.stagedVacations.push({
        user_id: this.vacation.user_id,
        userName: selectedUser ? selectedUser.name : "",
        start_date: this.vacation.start_date,
        end_date: this.vacation.end_date,
        number_of_days: this.vacation.number_of_days,
        pecuniary_allowance: this.vacation.pecuniary_allowance,
        error: null,
      });

      this.resetFormFields();
    },
    removeStagedRow(index) {
      const self = this;
      swal({
        title: "Deseja remover este registro da lista?",
        text: "Essa operação não pode ser desfeita",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: "Sim, remover!",
        showLoaderOnConfirm: true,
        buttons: {
          catch: { text: "Não", value: "cancel", className: "btn-danger" },
          confirm: { text: "Sim", value: "confirm" },
        },
      }).then((value) => {
        if (value === "confirm") {
          self.stagedVacations.splice(index, 1);
          swal("Sucesso", "Registro removido da lista!", "success");
        }
      });
    },
    hasPendingFormData() {
      return !!(this.vacation.user_id || this.dateInputs.start_date || this.dateInputs.end_date);
    },
    confirmPendingFormRowThenSubmitBatch() {
      const self = this;

      if (!self.hasPendingFormData()) {
        self.submitStagedBatch();
        return;
      }

      swal({
        title: "Você preencheu os dados de um colaborador",
        text: "Mas não clicou em Adicionar. Deseja incluir esse registro antes de salvar tudo?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Adicionar e Salvar",
        buttons: {
          catch: { text: "Descartar e Salvar", value: "discard", className: "btn-secondary" },
          confirm: { text: "Adicionar e Salvar", value: "add" },
        },
      }).then((value) => {
        if (value === "add") {
          self.normalizeDateField("start_date");
          self.normalizeDateField("end_date");
          if (!self.validateForm()) return;
          self.addToStaging();
          self.submitStagedBatch();
        } else if (value === "discard") {
          self.submitStagedBatch();
        }
      });
    },
    checkOverlaps(rows) {
      const self = this;
      const userIds = [...new Set(rows.map((row) => row.user_id))];
      const api = self.$store.state.api + "vacations";

      return axios
        .get(api, {
          params: {
            user_id: userIds,
            paginated: false,
            per_page: 9999,
            with: ["user"],
          },
        })
        .then((response) => {
          const existingVacations = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

          const conflicts = [];

          rows.forEach((row) => {
            const conflict = existingVacations.find((existing) => {
              if (existing.user_id !== row.user_id) return false;

              const existingStart = self.parseDisplayDate(existing.start_date);
              const existingEnd = self.parseDisplayDate(existing.end_date);
              if (!existingStart || !existingEnd) return false;

              return existingStart <= row.end_date && existingEnd >= row.start_date;
            });

            if (conflict) {
              conflicts.push({
                employeeName: row.userName,
                periodStart: conflict.start_date,
                periodEnd: conflict.end_date,
              });
            }
          });

          return conflicts;
        });
    },
    submitStagedBatch() {
      const self = this;
      const rows = self.stagedVacations;

      self.checkOverlaps(rows)
        .then((conflicts) => {
          if (conflicts.length > 0) {
            self.conflicts = conflicts;
            self.showConflictModal = true;
            return;
          }

          self.postStagedBatch(rows);
        })
        .catch((error) => {
          self.$message("Erro", apiErrorMessage(error), "error");
        });
    },
    postStagedBatch(rows) {
      const self = this;
      const api = self.$store.state.api + "vacations";

      Promise.allSettled(rows.map((row) => axios.post(api, row))).then((results) => {
        const failed = [];

        results.forEach((result, index) => {
          if (result.status === "rejected") {
            failed.push({
              ...rows[index],
              error: apiErrorMessage(result.reason),
            });
          }
        });

        self.stagedVacations = failed;

        const successCount = rows.length - failed.length;

        if (failed.length === 0) {
          self.$message("Sucesso", `${successCount} registro(s) de férias criado(s) com sucesso`, "success");
          self.$router.push("/vacations");
          return;
        }

        const failedNames = failed.map((row) => `${row.userName}: ${row.error}`).join("; ");
        self.$message(
          "Atenção",
          `${successCount} de ${rows.length} registros salvos. Falharam: ${failedNames}`,
          "warning"
        );
      });
    },
    save: function () {
      const self = this;

      if (self.stagedVacations.length > 0) {
        self.confirmPendingFormRowThenSubmitBatch();
        return;
      }

      self.normalizeDateField("start_date");
      self.normalizeDateField("end_date");

      if (!self.validateForm()) {
        return
      }

      const api = `${this.$store.state.api}vacations`

      axios
        .post(api, self.vacation)
        .then(response => {
          self.$message('Sucesso', 'Férias agendadas com sucesso', 'success')
          this.$router.push('/vacations')
        })
        .catch(error => {
          self.$message('Erro', apiErrorMessage(error), 'error')
        })
    },
    edit() {
      const self = this
      self.normalizeDateField('start_date')
      self.normalizeDateField('end_date')

      if (!self.validateForm()) {
        return
      }

      const api = `${self.$store.state.api}vacations/${self.vacation.id}`

      axios
        .put(api, self.vacation)
        .then(response => {
          self.$message(
            'Sucesso',
            'Férias atualizadas com sucesso',
            'success',
          )
          this.$router.push('/vacations')
        })
        .catch(error => {
          self.$message('Erro', apiErrorMessage(error), 'error')
        })
    },
    getVacation(id) {
      const self = this
      const api = `${self.$store.state.api}vacations/${id}`

      axios
        .get(api)
        .then(response => {
          const rows = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : []

          if (!rows.length) {
            self.$message('Erro', 'Registro de férias não encontrado.', 'error')
            self.$router.push('/vacations')
            return
          }

          self.vacation = self.normalizeVacationPayload(rows[0])
          self.dateInputs.start_date = self.formatDateForDisplay(self.vacation.start_date)
          self.dateInputs.end_date = self.formatDateForDisplay(self.vacation.end_date)
        })
        .catch(error => {
          self.$message('Erro', apiErrorMessage(error, 'Não foi possível carregar as férias.'), 'error')
          self.$router.push('/vacations')
        })
    },
    getUsers() {
      const self = this
      const api = `${self.$store.state.api}users/`

      self.loadingUsers = true

      axios
        .get(api, {
          params: {
            paginated: false,
            per_page: 9999,
          },
        })
        .then(response => {
          const rawUsers = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : []

          self.users = rawUsers.slice().sort((firstUser, secondUser) => {
            const firstName = (firstUser.name || '').toLowerCase()
            const secondName = (secondUser.name || '').toLowerCase()
            return firstName.localeCompare(secondName)
          })
        })
        .catch(error => {
          self.errors = error.response.data
          self.$message(null, self.errors, 'error')
        })
        .finally(() => {
          self.loadingUsers = false
        })
    },
  },
}
</script>

<style scoped>
.native-date-picker {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
}
</style>
